import type { BadgeVariants } from '~ds/components/ui/badge';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const CampaignStatus = {
  Published: 'published',
  Unpublished: 'unpublished',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CampaignStatus = (typeof CampaignStatus)[keyof typeof CampaignStatus];

/**
 * Get campaign status based on publishedAt.
 * - Published: Campaign has publishedAt set
 * - Unpublished: Campaign does not have publishedAt set
 */
export const getCampaignStatus = (campaign: { publishedAt?: string | null }): CampaignStatus => {
  return campaign.publishedAt ? CampaignStatus.Published : CampaignStatus.Unpublished;
};

export const getCampaignStatusColor = (status: CampaignStatus): NonNullable<BadgeVariants['tone']> => {
  switch (status) {
    case CampaignStatus.Published:
      return 'lime';
    case CampaignStatus.Unpublished:
      return 'neutral';
    default:
      return 'blue';
  }
};

/**
 * Get localized label for campaign status.
 */
export const getCampaignStatusLabel = (status: CampaignStatus, t: (key: string) => string): string => {
  return t(`content.campaigns.status.${status}`);
};

export const CampaignWindowStatus = {
  Unpublished: 'unpublished',
  Scheduled: 'scheduled',
  Live: 'live',
  Ended: 'ended',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type CampaignWindowStatus = (typeof CampaignWindowStatus)[keyof typeof CampaignWindowStatus];

/**
 * Where a campaign stands against its own window, the way a student-facing read filters it: a
 * published campaign only reaches students between its dates.
 */
export const getCampaignWindowStatus = (
  campaign: { publishedAt?: string | null; startDate?: string | null; endDate?: string | null },
  now: Date = new Date(),
): CampaignWindowStatus => {
  if (!campaign.publishedAt) {
    return CampaignWindowStatus.Unpublished;
  }

  if (campaign.startDate && dayjs.utc(campaign.startDate).toDate() > now) {
    return CampaignWindowStatus.Scheduled;
  }

  if (campaign.endDate && dayjs.utc(campaign.endDate).toDate() <= now) {
    return CampaignWindowStatus.Ended;
  }

  return CampaignWindowStatus.Live;
};

/**
 * Check if a campaign window has ended (endDate has passed).
 */
export const hasCampaignEnded = (campaign: { endDate?: string | null }): boolean => {
  if (!campaign.endDate) {
    return false;
  }

  return dayjs.utc(campaign.endDate).local().toDate() < new Date();
};

/**
 * Check if a campaign can be deleted.
 * Only unpublished campaigns can be deleted.
 */
export const canDeleteCampaign = (campaign: { publishedAt?: string | null; endDate?: string | null }): boolean => {
  const status = getCampaignStatus(campaign);
  // Unpublished campaigns can always be deleted
  // Published campaigns can be deleted if they have ended (endDate has passed)
  if (status === CampaignStatus.Unpublished) {
    return true;
  }

  // Published campaigns can be deleted if endDate has passed
  return hasCampaignEnded(campaign);
};

/**
 * Convert a campaign date-picker value (a calendar day, `YYYY-MM-DD`) to the UTC `Y-m-d H:i:s`
 * instant the API stores.
 *
 * A campaign date is a calendar day in the cluster's timezone, not a wall-clock instant. We anchor
 * the picked day to that timezone — the start to 00:00:00 and the end to 23:59:59 — and store the
 * resulting UTC instant, so a campaign "ending the 24th" stays live until 23:59:59 local. The
 * cluster timezone is served by the API (see `useClusterCountryConfig`), so the result is correct
 * regardless of the editor's own browser timezone.
 */
export const toCampaignDateTime = (
  date: string | null | undefined,
  timeZone: string,
  boundary: 'start' | 'end',
): string | null => {
  if (!date) {
    return null;
  }

  const day = dayjs.tz(date, timeZone);
  const anchored = boundary === 'end' ? day.endOf('day') : day.startOf('day');

  return anchored.utc().format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Inverse of {@link toCampaignDateTime}: resolve a stored UTC instant to the calendar day it falls
 * on in the cluster timezone, formatted with `format`. Defaults to `YYYY-MM-DD` for pre-filling the
 * date picker when editing; pass a display format (e.g. `DD MMM YYYY`) to render the day elsewhere.
 */
export const fromCampaignDateTime = (
  datetime: string | null | undefined,
  timeZone: string,
  format = 'YYYY-MM-DD',
): string | null => {
  if (!datetime) {
    return null;
  }

  return dayjs.utc(datetime).tz(timeZone).format(format);
};
